/**
 * Coquille de l'application.
 *
 * Le routage vit dans `router.ts` : voir ce module pour la raison (éviter un cycle
 * d'import entre l'accueil et la coquille).
 */

import { useEffect } from 'react';
import { useT } from './i18n/index.js';
import { Home } from './routes/Home.js';
import { Room } from './routes/Room.js';
import { navigate, useRoute } from './router.js';
import { LanguageSwitcher } from './ui/LanguageSwitcher.js';
import { InstallButton } from './ui/InstallButton.js';
import { ThemeSwitcher } from './ui/ThemeSwitcher.js';

export function App(): React.JSX.Element {
  const route = useRoute();
  const translator = useT();
  const isHome = route.name === 'home';

  // Le titre reflète l'écran : utile quand plusieurs partages sont ouverts dans des
  // onglets différents et qu'on cherche lequel est lequel.
  useEffect(() => {
    document.title = isHome ? translator.t('app.titleHome') : translator.t('app.titleRoom');
  }, [isHome, translator]);

  return (
    <div className="shell">
      <header className="header">
        <div className="container header-inner">
          <a
            className="brand"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
          >
            {/* Image décorative : le texte de marque juste à côté porte déjà
                l'information, un `alt` vide évite une redite pour les lecteurs d'écran. */}
            <img className="brand-mark" src="/icons/brand-mark.png" alt="" width={44} height={44} />
            {translator.t('app.brand')}
          </a>
          <div className="header-aside">
            <span className="faint">{translator.t('app.tagline')}</span>
            <InstallButton />
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="main">
        <div className="container">
          {route.name === 'home' ? <Home /> : <Room roomId={route.roomId} />}
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>{translator.t('app.footerMain')}</p>
          <p className="faint">{translator.t('app.footerDetail')}</p>
        </div>
      </footer>
    </div>
  );
}
